import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../api';
import { formatRoleLabel } from '../utils/roleManagement';
import { MAX_PASSWORD_LENGTH, validatePasswordComplex } from '../utils/credentials';
import { resizeImageFile } from '../utils/avatarImage';
import { THEMES, applyTheme, getStoredTheme, normalizeThemeId } from '../utils/theme';
import './UserOptions.css';

function UserOptions({ loggedInUser }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [username, setUsername] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [countryRegion, setCountryRegion] = useState('');
  const [bio, setBio] = useState('');
  const [favoriteFormats, setFavoriteFormats] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme);
  const [savingTheme, setSavingTheme] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarPending, setAvatarPending] = useState(false);
  const [avatarRotate, setAvatarRotate] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);

  const [clubLoading, setClubLoading] = useState(true);
  const [clubMembership, setClubMembership] = useState(null);
  const [clubInvites, setClubInvites] = useState([]);
  const [clubRequests, setClubRequests] = useState([]);
  const [clubBusy, setClubBusy] = useState(false);
  const [inviteConfirm, setInviteConfirm] = useState(null);

  const loadClubState = async () => {
    setClubLoading(true);
    try {
      const res = await apiFetch('/clubs/me');
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.message || 'Failed to load club info');
      setClubMembership(data.membership || null);
      setClubInvites(data.pending_invites || []);
      setClubRequests(data.pending_requests || []);
    } catch (err) {
      setError(err.message || 'Failed to load club info');
    } finally {
      setClubLoading(false);
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/auth/me');
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.message || 'Failed to load profile');
      }
      const profile = data.profile || {};
      setUsername(profile.username || data.user?.username || '');
      setRole(profile.role || data.role || data.user?.siteRole || '');
      setEmail(profile.email || '');
      setDisplayName(profile.display_name || '');
      setTimezone(profile.timezone || '');
      setCountryRegion(profile.country_region || '');
      setBio(profile.bio || '');
      setFavoriteFormats(profile.favorite_formats || '');
      setEmailNotifications(!!profile.email_notifications);
      const nextTheme = normalizeThemeId(profile.theme || getStoredTheme());
      setTheme(nextTheme);
      applyTheme(nextTheme);
      setAvatarUrl(profile.avatar_url || profile.avatarUrl || null);
      setAvatarPending(!!profile.avatar_pending || profile.avatar_status === 'pending');
      setAvatarRotate(!!profile.avatar_rotate);
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loggedInUser) {
      loadProfile();
      loadClubState();
    } else {
      setLoading(false);
      setClubLoading(false);
    }
  }, [loggedInUser]);

  const flash = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(null), 4000);
  };

  const handleThemeChange = async (nextId) => {
    const id = normalizeThemeId(nextId);
    setTheme(id);
    applyTheme(id);
    setSavingTheme(true);
    setError(null);
    try {
      const res = await apiFetch('/auth/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ theme: id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.message || 'Failed to save theme');
      }
      const saved = normalizeThemeId(data.profile?.theme || id);
      setTheme(saved);
      applyTheme(saved);
      flash('Theme saved');
    } catch (err) {
      setError(err.message || 'Failed to save theme');
    } finally {
      setSavingTheme(false);
    }
  };

  const saveAvatarSettings = async (body) => {
    if (body.avatar_rotate !== undefined) setAvatarRotate(!!body.avatar_rotate);
    try {
      const res = await apiFetch('/auth/me/avatar-settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || 'Failed to save');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setError(null);
    try {
      const res = await apiFetch('/auth/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          display_name: displayName,
          timezone,
          country_region: countryRegion,
          bio,
          favorite_formats: favoriteFormats,
          email_notifications: emailNotifications,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.message || 'Failed to save profile');
      }
      const profile = data.profile || {};
      setEmail(profile.email || '');
      setDisplayName(profile.display_name || '');
      setTimezone(profile.timezone || '');
      setCountryRegion(profile.country_region || '');
      setBio(profile.bio || '');
      setFavoriteFormats(profile.favorite_formats || '');
      setEmailNotifications(!!profile.email_notifications);
      flash('Profile saved');
    } catch (err) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLeaveClub = async () => {
    if (!clubMembership?.club_id) return;
    if (!window.confirm(`Leave ${clubMembership.club_name}?`)) return;
    setClubBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/clubs/${clubMembership.club_id}/leave`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.message || 'Failed to leave club');
      flash('Left club');
      await loadClubState();
    } catch (err) {
      setError(err.message);
    } finally {
      setClubBusy(false);
    }
  };

  const handleAcceptInvite = async (inviteId, confirm = false) => {
    setClubBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/clubs/invites/${inviteId}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.requires_confirmation) {
        setInviteConfirm({
          inviteId,
          message: data.message,
          currentClub: data.current_club_name,
          targetClub: data.target_club_name,
        });
        return;
      }
      if (!res.ok || !data.ok) throw new Error(data.message || 'Failed to accept invite');
      setInviteConfirm(null);
      flash('Invite accepted');
      await loadClubState();
    } catch (err) {
      setError(err.message);
    } finally {
      setClubBusy(false);
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    setClubBusy(true);
    try {
      const res = await apiFetch(`/clubs/invites/${inviteId}/decline`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.message || 'Failed to decline invite');
      flash('Invite declined');
      await loadClubState();
    } catch (err) {
      setError(err.message);
    } finally {
      setClubBusy(false);
    }
  };

  const handleCancelRequest = async (clubId) => {
    setClubBusy(true);
    try {
      const res = await apiFetch(`/clubs/${clubId}/request`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.message || 'Failed to cancel request');
      flash('Join request cancelled');
      await loadClubState();
    } catch (err) {
      setError(err.message);
    } finally {
      setClubBusy(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }
    const passwordError = validatePasswordComplex(newPassword, { label: 'New password' });
    if (passwordError) {
      setError(passwordError);
      return;
    }
    setSavingPassword(true);
    setError(null);
    try {
      const res = await apiFetch('/auth/me/password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.message || 'Failed to change password');
      }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      flash('Password updated successfully');
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  if (!loggedInUser) {
    return (
      <div className="user-options">
        <p className="user-options-login-prompt">
          Please <Link to="/">log in</Link> to manage your account settings.
        </p>
      </div>
    );
  }

  if (loading) {
    return <div className="user-options"><p>Loading account settings…</p></div>;
  }

  return (
    <div className="user-options">
      <h1>Account Settings</h1>

      <div className="user-options-warning" role="note">
        Most fields below are optional. Leaving them blank will not block you from playing now,
        but may limit future features such as tournaments, account recovery, notifications,
        and personalized matchmaking.
      </div>

      {message && <p className="user-options-message user-options-message--success">{message}</p>}
      {error && <p className="user-options-message user-options-message--error">{error}</p>}

      <section className="user-options-section user-options-theme">
        <h2>Appearance</h2>
        <p className="user-options-hint">
          Themes change colors only — table layout and controls stay the same.
          Available themes are loaded from theme files; removed themes fall back to the site default.
        </p>
        <div className="user-options-theme-grid" role="radiogroup" aria-label="Color theme">
          {THEMES.map((t) => {
            const selected = theme === t.id;
            const sw = t.swatches || {};
            return (
              <button
                key={t.id}
                type="button"
                role="radio"
                aria-checked={selected}
                className={[
                  'user-options-theme-card',
                  selected ? 'user-options-theme-card--selected' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => handleThemeChange(t.id)}
                disabled={savingTheme}
              >
                <span className="user-options-theme-swatches" aria-hidden="true">
                  <span
                    className="user-options-theme-swatch user-options-theme-swatch--bg"
                    style={{ background: sw.bg }}
                  />
                  <span
                    className="user-options-theme-swatch user-options-theme-swatch--fg"
                    style={{ background: sw.fg }}
                  />
                  <span
                    className="user-options-theme-swatch user-options-theme-swatch--accent"
                    style={{ background: sw.accent }}
                  />
                </span>
                <span className="user-options-theme-label">{t.label}</span>
                <span className="user-options-theme-desc">{t.description}</span>
                {selected && <span className="user-options-theme-check">Selected</span>}
              </button>
            );
          })}
        </div>
      </section>

      <section className="user-options-section user-options-club">
        <h2>Club membership</h2>
        <p className="user-options-hint">
          Browse and apply to clubs on{' '}
          <a href="/clubs.html" className="user-options-external-link">fadeddreamz.com/clubs</a>.
          You may belong to one club at a time.
        </p>
        {clubLoading ? (
          <p>Loading club info…</p>
        ) : (
          <>
            {clubMembership ? (
              <div className="user-options-club-current">
                <strong>{clubMembership.club_name}</strong>
                <span className="user-options-club-badge">Current club</span>
                <button
                  type="button"
                  className="user-options-club-leave"
                  onClick={handleLeaveClub}
                  disabled={clubBusy}
                >
                  Leave club
                </button>
              </div>
            ) : (
              <p className="user-options-hint">You are not in a club yet.</p>
            )}

            {clubInvites.length > 0 && (
              <div className="user-options-club-block">
                <h3>Pending invites</h3>
                <ul className="user-options-club-list">
                  {clubInvites.map((inv) => (
                    <li key={inv.id}>
                      <span>{inv.club_name}</span>
                      {inv.invited_by_username && (
                        <span className="user-options-hint"> from {inv.invited_by_username}</span>
                      )}
                      <div className="user-options-club-actions">
                        <button
                          type="button"
                          onClick={() => handleAcceptInvite(inv.id)}
                          disabled={clubBusy}
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          className="user-options-club-secondary"
                          onClick={() => handleDeclineInvite(inv.id)}
                          disabled={clubBusy}
                        >
                          Decline
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {clubRequests.length > 0 && (
              <div className="user-options-club-block">
                <h3>Pending join requests</h3>
                <ul className="user-options-club-list">
                  {clubRequests.map((req) => (
                    <li key={req.id}>
                      <span>{req.club_name}</span>
                      <span className="user-options-hint"> — awaiting approval</span>
                      <button
                        type="button"
                        className="user-options-club-secondary"
                        onClick={() => handleCancelRequest(req.club_id)}
                        disabled={clubBusy}
                      >
                        Cancel request
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
        {inviteConfirm && (
          <div className="user-options-club-confirm" role="alert">
            <p>{inviteConfirm.message}</p>
            <div className="user-options-club-actions">
              <button
                type="button"
                onClick={() => handleAcceptInvite(inviteConfirm.inviteId, true)}
                disabled={clubBusy}
              >
                Leave {inviteConfirm.currentClub} and join {inviteConfirm.targetClub}
              </button>
              <button
                type="button"
                className="user-options-club-secondary"
                onClick={() => setInviteConfirm(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="user-options-section">
        <h2>Account</h2>
        <div className="user-options-readonly-grid">
          <label>
            Username
            <input type="text" value={username} readOnly disabled />
          </label>
          <label>
            Assigned role
            <input type="text" value={formatRoleLabel(role)} readOnly disabled />
          </label>
        </div>
      </section>

      <section className="user-options-section">
        <h2>Player profile</h2>
        <p className="user-options-hint">All fields optional — add what you are comfortable sharing.</p>
        <form onSubmit={handleSaveProfile} className="user-options-form">
          <label>
            Display name
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Nickname shown in lobbies"
              maxLength={48}
              autoComplete="nickname"
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
            <span className="user-options-field-note">
              Public domains only (.com, .gov, .net, .store, .org, .edu, etc.). .local is not allowed.
            </span>
          </label>
          <label>
            Timezone
            <input
              type="text"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              placeholder="e.g. America/New_York, UTC+0"
              maxLength={64}
              list="timezone-suggestions"
            />
            <datalist id="timezone-suggestions">
              <option value="America/New_York" />
              <option value="America/Chicago" />
              <option value="America/Denver" />
              <option value="America/Los_Angeles" />
              <option value="Europe/London" />
              <option value="Europe/Paris" />
              <option value="Asia/Tokyo" />
              <option value="Australia/Sydney" />
              <option value="UTC" />
            </datalist>
          </label>
          <label>
            Country / region
            <input
              type="text"
              value={countryRegion}
              onChange={(e) => setCountryRegion(e.target.value)}
              placeholder="e.g. US, UK, Canada"
              maxLength={64}
              autoComplete="country"
            />
          </label>
          <label>
            Favorite formats
            <input
              type="text"
              value={favoriteFormats}
              onChange={(e) => setFavoriteFormats(e.target.value)}
              placeholder="e.g. Standard, Commander, Draft"
              maxLength={256}
            />
          </label>
          <label>
            About
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="A short note about your play style or experience"
              rows={3}
              maxLength={500}
            />
          </label>
          <label className="user-options-checkbox">
            <input
              type="checkbox"
              checked={emailNotifications}
              onChange={(e) => setEmailNotifications(e.target.checked)}
            />
            <span>Email me about tournaments and site updates (when email is set)</span>
          </label>
          <button type="submit" disabled={savingProfile}>
            {savingProfile ? 'Saving…' : 'Save profile'}
          </button>
        </form>
      </section>

      <section className="user-options-section user-options-avatars">
        <h2>Avatars</h2>
        <p className="user-options-hint">
          Square images up to 512×512. You see your avatar immediately; others see it after approval.
        </p>
        <div className="user-options-avatar-grid user-options-avatar-grid--solo">
          <div className="user-options-avatar-block">
            <h3>Your avatar</h3>
            <div className="user-options-avatar-preview">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="user-options-avatar-img" />
              ) : (
                <span className="user-options-avatar-placeholder">?</span>
              )}
            </div>
            {avatarPending && <p className="user-options-avatar-pending">Pending approval</p>}
            <label className="user-options-checkbox">
              <input
                type="checkbox"
                checked={avatarRotate}
                onChange={(e) => saveAvatarSettings({ avatar_rotate: e.target.checked })}
              />
              <span>Auto-rotate at table (top faces away from center)</span>
            </label>
            <label className="user-options-file">
              <span>Upload avatar</span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                disabled={avatarBusy}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setAvatarBusy(true);
                  try {
                    const resized = await resizeImageFile(file);
                    const fd = new FormData();
                    fd.append('avatar', resized);
                    const res = await apiFetch('/auth/me/avatar', { method: 'POST', body: fd });
                    const data = await res.json();
                    if (!res.ok || !data.ok) throw new Error(data.message || 'Upload failed');
                    setAvatarUrl(data.profile?.avatar_url || null);
                    setAvatarPending(!!data.profile?.avatar_pending);
                    flash(data.message);
                  } catch (err) {
                    setError(err.message);
                  } finally {
                    setAvatarBusy(false);
                    e.target.value = '';
                  }
                }}
              />
            </label>
          </div>
        </div>
      </section>

      <section className="user-options-section">
        <h2>Password</h2>
        <form onSubmit={handleChangePassword} className="user-options-form">
          <label>
            Current password
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value.slice(0, MAX_PASSWORD_LENGTH))}
              autoComplete="current-password"
              maxLength={MAX_PASSWORD_LENGTH}
              required
            />
          </label>
          <label>
            New password
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value.slice(0, MAX_PASSWORD_LENGTH))}
              autoComplete="new-password"
              minLength={8}
              maxLength={MAX_PASSWORD_LENGTH}
              required
            />
          </label>
          <label>
            Confirm new password
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value.slice(0, MAX_PASSWORD_LENGTH))}
              autoComplete="new-password"
              minLength={8}
              maxLength={MAX_PASSWORD_LENGTH}
              required
            />
          </label>
          <p className="user-options-hint">
            Password must be 8+ characters with upper, lower, number, and special character.
          </p>
          <button type="submit" disabled={savingPassword}>
            {savingPassword ? 'Updating…' : 'Change password'}
          </button>
        </form>
      </section>
    </div>
  );
}

export default UserOptions;